/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { CosmosText, SlimContactCard, LicenseMainCard, LicenseSeatData, LicenseTopCard } from "@components/elements";
import {
	CosmosEnumCountryCodes,
	SlimContactCardFragment,
	LicenseMainCardFragment,
	LicenseTopCardFragment,
} from "@cosmos/hygraph-schema";
import { useGeo } from "@cosmos/next-hooks";

import { css } from "@panda/css";
import { HStack, VStack } from "@panda/jsx";

export interface LicenseCardsProps {
	locale: string;
	freeCardHeading?: string | null;
	freeCard?: LicenseTopCardFragment | null;
	cardHeading?: string | null;
	showExampleLabel?: string | null;
	hideExampleLabel?: string | null;
	mainCards: Array<LicenseMainCardFragment>;
	seatsData1?: LicenseSeatData;
	seatsData2?: LicenseSeatData;
	contactCard?: SlimContactCardFragment | null;
	contactCard2?: SlimContactCardFragment | null;
}

export const LicenseCards = ({
	freeCardHeading,
	freeCard,
	cardHeading,
	showExampleLabel,
	hideExampleLabel,
	mainCards,
	contactCard,
	contactCard2,
	locale,
	seatsData1,
	seatsData2,
}: LicenseCardsProps): React.JSX.Element => {
	const { geo, isLoading: geoLoading } = useGeo();
	let country = CosmosEnumCountryCodes.Us;
	if (geo && !geoLoading && geo.country) {
		//we want the geo to load and use the country
		country = geo?.country as CosmosEnumCountryCodes;
	}

	return (
		<VStack className="license-cards" alignItems="stretch" gap="eds.dimension.48">
			<VStack alignItems="flex-start" gap="eds.dimension.24">
				<CosmosText variant="headingLG" as="h2" text={freeCardHeading || ""} />
				<LicenseTopCard {...(freeCard || ({} as LicenseTopCardFragment))} />
			</VStack>
			<VStack alignItems="flex-start" gap="eds.dimension.24">
				<CosmosText variant="headingLG" as="h2" text={cardHeading || ""} />
				<VStack alignItems="stretch" gap="eds.dimension.16">
					<HStack gap="eds.dimension.16" alignItems="stretch" className={css({ lgDown: { flexDirection: "column" } })}>
						{mainCards.slice(0, 2).map((card, i) => (
							<LicenseMainCard
								key={`license-main-card-${i}`}
								className={css({ flexBasis: "50%" })}
								locale={locale}
								showExampleLabel={showExampleLabel}
								hideExampleLabel={hideExampleLabel}
								defaultPriceNum={i === 0 ? seatsData1?.defaultPriceNum : seatsData2?.defaultPriceNum}
								defaultPriceStr={i === 0 ? seatsData1?.defaultPriceStr : seatsData2?.defaultPriceStr}
								seatOfferId={i === 0 ? seatsData1?.seatOfferId : seatsData2?.seatOfferId}
								seatNamespace={i === 0 ? seatsData1?.seatNamespace : seatsData2?.seatNamespace}
								seatPurchaseUrl={i === 0 ? seatsData1?.seatPurchaseUrl : seatsData2?.seatPurchaseUrl}
								country={country}
								{...card}
							/>
						))}
					</HStack>
					<SlimContactCard {...(contactCard || ({} as SlimContactCardFragment))} />
					<SlimContactCard {...(contactCard2 || ({} as SlimContactCardFragment))} />
				</VStack>
			</VStack>
		</VStack>
	);
};
